/* Frequently bought together.
   Rows start unchecked; toggling a row (or its variant/quantity) recomputes a
   running total, and the add button adds every checked row in one /cart/add.js
   request — the multi-item pattern the theme already uses in bundle-builder.js
   (cart.renderContents + is-empty clear + cartUpdate). The current product's
   row follows the page's variant picker instead of carrying its own select. */
if (!customElements.get('bought-together')) {
  const moneyFormat = () => (window.theme && window.theme.moneyFormat) || '${{amount}}';

  class BoughtTogether extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.sectionId = this.dataset.sectionId || '';
      this.cartType = this.dataset.cartType || '';
      this.showSavings = this.dataset.showSavings !== 'false';
      this.strings = this.readStrings();

      this.items = Array.from(this.querySelectorAll('[data-bt-item]'));
      this.addButton = this.querySelector('[data-bt-add]');
      this.addLabelEl = this.querySelector('[data-bt-add-label]');
      this.buttonTemplate = this.addLabelEl ? this.addLabelEl.textContent.trim() : '';
      this.totalEl = this.querySelector('[data-bt-total]');
      this.compareTotalEl = this.querySelector('[data-bt-compare-total]');
      this.countEl = this.querySelector('[data-bt-count]');
      this.saveEl = this.querySelector('[data-bt-save]');
      this.errorEl = this.querySelector('[data-bt-error]');

      this.items.forEach((item) => this.wireRow(item));
      this.wireCurrentSync();
      if (this.addButton) this.addButton.addEventListener('click', () => this.add());

      this.update();
    }

    readStrings() {
      const el = this.querySelector('[data-bt-strings]');
      let parsed = {};
      if (el) {
        try {
          parsed = JSON.parse(el.textContent);
        } catch (error) {
          parsed = {};
        }
      }
      return Object.assign(
        {
          add_error: 'Could not add these items to your cart. Please try again.',
          added: 'Added',
          item_one: 'item',
          item_other: 'items',
          save: 'Save {{ amount }}',
        },
        parsed
      );
    }

    wireRow(item) {
      const check = item.querySelector('[data-bt-check]');
      const variant = item.querySelector('[data-bt-variant]');
      const qty = item.querySelector('[data-bt-qty]');

      if (check) check.addEventListener('change', () => this.update());
      if (variant && variant.tagName === 'SELECT') {
        variant.addEventListener('change', () => this.onVariantChange(item));
      }
      if (qty) qty.addEventListener('change', () => this.update());

      // The whole row toggles selection, except clicks on a link or a control.
      item.addEventListener('click', (event) => {
        if (event.target.closest('a, select, option, input, .bought-together__field')) return;
        if (!check || check.disabled) return;
        check.checked = !check.checked;
        this.update();
      });
    }

    // The current product's row mirrors the page's variant picker rather than
    // carrying its own select, so "Add" always adds the variant the shopper sees.
    wireCurrentSync() {
      this.currentItem = this.items.find((item) => item.hasAttribute('data-bt-current'));
      if (!this.currentItem) return;

      const script = this.currentItem.querySelector('[data-bt-variants]');
      if (script) {
        try {
          this.currentVariants = JSON.parse(script.textContent);
        } catch (error) {
          this.currentVariants = [];
        }
      }

      const input = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
      if (!input || !this.currentVariants) return;

      this.currentInput = input;
      this.syncCurrent();
      input.addEventListener('change', () => this.syncCurrent());
    }

    syncCurrent() {
      const id = Number(this.currentInput.value);
      const variant = (this.currentVariants || []).find((entry) => Number(entry.id) === id);
      if (!variant) return;

      const item = this.currentItem;
      const hidden = item.querySelector('[data-bt-variant]');
      if (hidden) {
        hidden.value = variant.id;
        hidden.dataset.price = variant.price;
        hidden.dataset.compare = variant.compare_at_price || 0;
      }

      const priceEl = item.querySelector('[data-bt-price]');
      const compareEl = item.querySelector('[data-bt-compare]');
      if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = this.formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      const titleEl = item.querySelector('[data-bt-variant-title]');
      if (titleEl) titleEl.textContent = (variant.title || '').replace(' / ', ' · ');

      const check = item.querySelector('[data-bt-check]');
      if (variant.available === false) {
        item.classList.add('is-unavailable');
        if (check) {
          check.checked = false;
          check.disabled = true;
        }
      } else {
        item.classList.remove('is-unavailable');
        if (check) check.disabled = false;
      }

      this.update();
    }

    rowState(item) {
      const check = item.querySelector('[data-bt-check]');
      const variantEl = item.querySelector('[data-bt-variant]');
      const qtyEl = item.querySelector('[data-bt-qty]');

      let variantId = null;
      let price = 0;
      let compare = 0;
      if (variantEl && variantEl.tagName === 'SELECT') {
        const opt = variantEl.options[variantEl.selectedIndex];
        variantId = variantEl.value;
        price = Number(opt && opt.dataset.price) || 0;
        compare = Number(opt && opt.dataset.compare) || 0;
      } else if (variantEl) {
        variantId = variantEl.value;
        price = Number(variantEl.dataset.price) || 0;
        compare = Number(variantEl.dataset.compare) || 0;
      }

      const quantity = qtyEl ? Number(qtyEl.value) || 1 : 1;
      const selected = !!(check && check.checked && !check.disabled && variantId);
      return { selected, variantId, price, compare: compare > price ? compare : 0, quantity };
    }

    onVariantChange(item) {
      const variantEl = item.querySelector('[data-bt-variant]');
      if (!variantEl || variantEl.tagName !== 'SELECT') return;
      const opt = variantEl.options[variantEl.selectedIndex];
      const price = Number(opt && opt.dataset.price) || 0;
      const compare = Number(opt && opt.dataset.compare) || 0;

      const priceEl = item.querySelector('[data-bt-price]');
      const compareEl = item.querySelector('[data-bt-compare]');
      if (priceEl) priceEl.textContent = this.formatMoney(price);
      if (compareEl) {
        if (compare > price) {
          compareEl.textContent = this.formatMoney(compare);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      this.update();
    }

    update() {
      let total = 0;
      let compareTotal = 0;
      let count = 0;

      this.items.forEach((item) => {
        const state = this.rowState(item);
        item.classList.toggle('is-selected', state.selected);
        if (!state.selected) return;
        total += state.price * state.quantity;
        compareTotal += (state.compare || state.price) * state.quantity;
        count += state.quantity;
      });

      if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);

      const hasSavings = this.showSavings && compareTotal > total;
      if (this.compareTotalEl) {
        if (hasSavings) {
          this.compareTotalEl.textContent = this.formatMoney(compareTotal);
          this.compareTotalEl.hidden = false;
        } else {
          this.compareTotalEl.hidden = true;
        }
      }
      if (this.saveEl) {
        if (hasSavings) {
          this.saveEl.textContent = this.strings.save.replace('{{ amount }}', this.formatMoney(compareTotal - total));
          this.saveEl.hidden = false;
        } else {
          this.saveEl.hidden = true;
        }
      }
      if (this.countEl) {
        this.countEl.textContent = this.countLabel(count);
        this.countEl.hidden = count === 0;
      }
      if (this.addLabelEl && this.buttonTemplate.indexOf('{{') !== -1) {
        this.addLabelEl.textContent = this.buttonTemplate.replace('{{ count }}', count);
      }

      if (this.addButton && !this.addButton.classList.contains('is-loading') && !this.addButton.classList.contains('is-added')) {
        this.addButton.disabled = count === 0;
      }
      this.hideError();
    }

    countLabel(count) {
      const word = count === 1 ? this.strings.item_one : this.strings.item_other;
      return `${count} ${word}`;
    }

    async add() {
      const items = this.items
        .map((item) => this.rowState(item))
        .filter((state) => state.selected)
        .map((state) => ({ id: Number(state.variantId), quantity: state.quantity }));
      if (!items.length) return;

      this.setLoading(true);
      this.hideError();

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items, sections_url: window.location.pathname };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
      } else {
        body.sections = ['cart-icon-bubble'];
      }

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok) {
          this.showError(data.description || data.message || this.strings.add_error);
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartError) {
            window.publish(window.PUB_SUB_EVENTS.cartError, { source: 'bought-together', message: data.description });
          }
          return;
        }

        // The multi-item /cart/add.js response has no top-level `id`; cart-notification
        // needs one to render its "just added" line, so seed it from a returned line.
        const lead = data.items && data.items[0];
        if (lead && data.id == null) data.id = lead.variant_id || lead.id;

        if (this.cartType === 'page') {
          window.location = `${root}cart`;
          return;
        }

        if (cart && typeof cart.renderContents === 'function') {
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
          cart.renderContents(data);
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          this.publishUpdate(data);
          this.showAdded();
        } else {
          // No drawer/notification on the page: refresh the header bubble, then go to the cart.
          this.updateBubble(data);
          this.publishUpdate(data);
          window.location = `${root}cart`;
        }
      } catch (error) {
        this.showError(this.strings.add_error);
      } finally {
        this.setLoading(false);
      }
    }

    publishUpdate(data) {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bought-together', cartData: data });
      }
    }

    updateBubble(data) {
      const html = data.sections && data.sections['cart-icon-bubble'];
      if (!html) return;
      const inner = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (!inner) return;
      document.querySelectorAll('#cart-icon-bubble').forEach((el) => {
        el.innerHTML = inner.innerHTML;
      });
    }

    setLoading(on) {
      if (!this.addButton) return;
      this.addButton.classList.toggle('is-loading', on);
      this.addButton.setAttribute('aria-busy', on ? 'true' : 'false');
      if (on) {
        this.addButton.disabled = true;
      } else if (!this.addButton.classList.contains('is-added')) {
        this.update();
      }
    }

    showAdded() {
      if (!this.addButton || !this.addLabelEl) return;
      const restore = this.addLabelEl.textContent;
      this.addButton.classList.add('is-added');
      this.addButton.disabled = true;
      this.addLabelEl.textContent = this.strings.added;
      clearTimeout(this._addedTimer);
      this._addedTimer = setTimeout(() => {
        this.addButton.classList.remove('is-added');
        this.addLabelEl.textContent = restore;
        this.update();
      }, 1500);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    // Standard Shopify money formatting, driven by window.theme.moneyFormat
    // (which already honours the currency-code display setting).
    formatMoney(cents) {
      const format = moneyFormat();
      const value = Number(cents) || 0;
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholder);
      const token = match ? match[1] : 'amount';

      const withDelimiters = (amount, precision, thousands, decimal) => {
        precision = precision == null ? 2 : precision;
        thousands = thousands || ',';
        decimal = decimal || '.';
        const fixed = (amount / 100).toFixed(precision);
        const parts = fixed.split('.');
        const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        return whole + (parts[1] ? decimal + parts[1] : '');
      };

      let formatted;
      switch (token) {
        case 'amount_no_decimals':
          formatted = withDelimiters(value, 0);
          break;
        case 'amount_with_comma_separator':
          formatted = withDelimiters(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = withDelimiters(value, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          formatted = withDelimiters(value, 2, "'", '.');
          break;
        case 'amount_with_space_separator':
          formatted = withDelimiters(value, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          formatted = withDelimiters(value, 0, ' ', ',');
          break;
        default:
          formatted = withDelimiters(value, 2);
      }
      return format.replace(placeholder, formatted);
    }
  }

  customElements.define('bought-together', BoughtTogether);
}
